(function () {
  'use strict';

  const instances = new Map();
  const MOBILE_MQ = '(max-width: 768px)';
  const SWIPE_THRESHOLD = 50;
  const DESKTOP_SLIDE_DURATION_MS = 650;

  function measureHeaderOffset() {
    const storeHeader = document.querySelector('store-header');
    if (storeHeader) {
      const bottom = Math.round(storeHeader.getBoundingClientRect().bottom);
      if (bottom > 0) return bottom;

      const height = Math.round(storeHeader.offsetHeight);
      if (height > 0) return height;
    }

    const headerSection = document.getElementById('shopify-section-header');
    if (headerSection?.offsetHeight > 0) {
      return Math.round(headerSection.offsetHeight);
    }

    const fromVar = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--header-height')
    );
    if (!Number.isNaN(fromVar) && fromVar > 0) return Math.round(fromVar);

    return 0;
  }

  class OfShowcaseSlider {
    constructor(section) {
      this.section = section;
      this.scrollContainer = section.querySelector('[data-of-showcase-scroll-container]');
      this.pin = section.querySelector('[data-of-showcase-pin]');
      this.viewport = section.querySelector('[data-of-showcase-viewport]');
      this.track = section.querySelector('[data-of-showcase-track]');
      this.slides = section.querySelectorAll('[data-of-showcase-slide]');
      this.prevBtn = section.querySelector('[data-of-showcase-prev]');
      this.nextBtn = section.querySelector('[data-of-showcase-next]');
      this.dots = section.querySelectorAll('[data-of-showcase-dot]');

      if (!this.scrollContainer || !this.pin || !this.viewport || !this.track || !this.slides.length) {
        return;
      }

      this.headerOffset = 0;
      this.maxScroll = 0;
      this.currentIndex = 0;
      this.autoplayTimer = null;
      this.autoplayResumeTimer = null;
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.touchDeltaX = 0;
      this.isSwiping = false;
      this.isMobile = window.matchMedia(MOBILE_MQ);
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.wheelLockedUntil = 0;
      this.wheelCooldownMs = DESKTOP_SLIDE_DURATION_MS + 80;
      this.wheelAnimating = false;
      this.slideTransitionMs = DESKTOP_SLIDE_DURATION_MS;

      this.readAutoplayConfig();

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onModeChange = this.onModeChange.bind(this);
      this.onPrev = this.onPrev.bind(this);
      this.onNext = this.onNext.bind(this);
      this.onDotClick = this.onDotClick.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchMove = this.onTouchMove.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      this.onWheel = this.onWheel.bind(this);

      if (this.prefersReducedMotion) {
        this.section.classList.add('is-reduced-motion');
        if (!window.matchMedia(MOBILE_MQ).matches) {
          return;
        }
      }

      this.init();
    }

    readAutoplayConfig() {
      this.autoplayEnabled = this.section.dataset.mobileAutoplay === 'true';
      const interval = Number.parseInt(this.section.dataset.mobileAutoplayInterval, 10);
      this.autoplayInterval = Number.isNaN(interval) ? 5000 : interval * 1000;
    }

    init() {
      this.syncHeaderOffset();
      this.measure();
      this.bindEvents();

      window.requestAnimationFrame(() => {
        this.syncHeaderOffset();
        this.measure();
      });

      [100, 300, 800].forEach((delay) => {
        window.setTimeout(() => {
          if (!this.section.isConnected) return;
          this.syncHeaderOffset();
          this.measure();
        }, delay);
      });

      window.requestAnimationFrame(() => {
        this.syncSlideVisualState(this.currentIndex, { animate: false });
        this.section.classList.add('is-ss-ready');
      });

      if (window.customElements?.whenDefined) {
        customElements.whenDefined('store-header').then(() => {
          if (!this.section.isConnected) return;
          this.syncHeaderOffset();
          this.measure();
        });
      }
    }

    isMobileMode() {
      return this.isMobile.matches;
    }

    shouldUseMobileCarousel() {
      return this.isMobileMode() && this.slides.length > 1;
    }

    syncHeaderOffset() {
      const next = measureHeaderOffset();
      if (next <= 0 && this.headerOffset > 0) return;
      this.headerOffset = next;
      this.section.style.setProperty('--of-ss-header-offset', `${next}px`);
    }

    updateScrollContainerHeight() {
      if (this.isMobileMode() || this.prefersReducedMotion) {
        this.scrollContainer.style.height = '';
        return;
      }

      if (this.slides.length <= 1 || this.maxScroll <= 0) {
        this.scrollContainer.style.height = '';
        return;
      }

      this.scrollContainer.style.height = `${this.slides.length * 100}vh`;
    }

    measure() {
      this.setSlideWidths();
      this.maxScroll = Math.max(0, this.track.scrollWidth - this.viewport.clientWidth);
      this.updateScrollContainerHeight();

      if (this.isMobileMode() || this.prefersReducedMotion) {
        this.scrollContainer.style.height = '';
        if (this.isMobileMode()) {
          this.goToSlide(this.currentIndex);
          this.syncAutoplay();
        } else {
          this.stopAutoplay();
          this.track.style.transform = '';
        }
        return;
      }

      this.stopAutoplay();
      this.onScroll();
    }

    setSlideWidths() {
      const panelWidth = this.viewport.clientWidth;
      this.slides.forEach((slide) => {
        slide.style.flex = `0 0 ${panelWidth}px`;
        slide.style.width = `${panelWidth}px`;
      });
    }

    setTrackOffset(value) {
      const x = Math.max(0, Math.min(this.maxScroll, value));
      this.track.style.transform = x > 0 ? `translate3d(-${x}px, 0, 0)` : '';
    }

    getSlideOffset(index) {
      const slide = this.slides[index];
      return slide ? slide.offsetLeft : 0;
    }

    getMaxInternalScrollY() {
      return Math.max(0, this.scrollContainer.offsetHeight - window.innerHeight);
    }

    getInternalScrollY() {
      const rect = this.scrollContainer.getBoundingClientRect();
      const maxScrollY = this.getMaxInternalScrollY();
      const scrollY = this.headerOffset - rect.top;
      return Math.max(0, Math.min(maxScrollY, scrollY));
    }

    getInternalScrollYForIndex(index) {
      const maxScrollY = this.getMaxInternalScrollY();
      if (this.slides.length <= 1 || maxScrollY <= 0) return 0;
      return (index / (this.slides.length - 1)) * maxScrollY;
    }

    getIndexFromInternalScrollY(scrollY) {
      const maxScrollY = this.getMaxInternalScrollY();
      if (this.slides.length <= 1 || maxScrollY <= 0) return 0;

      const segment = maxScrollY / (this.slides.length - 1);
      const index = Math.round(scrollY / segment);
      return Math.max(0, Math.min(this.slides.length - 1, index));
    }

    isWheelCaptureActive() {
      if (this.isMobileMode() || this.prefersReducedMotion) return false;
      if (this.slides.length <= 1) return false;

      const rect = this.scrollContainer.getBoundingClientRect();
      const maxScrollY = this.getMaxInternalScrollY();
      if (maxScrollY <= 0) return false;

      return rect.top <= this.headerOffset + 2 && rect.bottom >= window.innerHeight;
    }

    scrollToIndex(index) {
      const maxScrollY = this.getMaxInternalScrollY();
      const currentInternalY = this.getInternalScrollY();
      const targetInternalY = this.getInternalScrollYForIndex(index);
      const delta = targetInternalY - currentInternalY;

      if (Math.abs(delta) < 1) return;

      this.wheelAnimating = true;
      window.scrollTo({
        top: window.scrollY + delta,
        behavior: 'smooth',
      });

      window.setTimeout(() => {
        this.wheelAnimating = false;
      }, this.slideTransitionMs);
    }

    syncSlideVisualState(nextIndex, options = {}) {
      const prevIndex = this.currentIndex;
      const useDesktopMotion =
        options.animate !== false && !this.isMobileMode() && !this.prefersReducedMotion && this.section.classList.contains('is-ss-ready');

      if (useDesktopMotion && nextIndex !== prevIndex) {
        this.section.classList.remove('is-slide-forward', 'is-slide-backward');
        this.section.classList.add(nextIndex > prevIndex ? 'is-slide-forward' : 'is-slide-backward');
      }

      this.slides.forEach((slide, index) => {
        slide.classList.toggle('is-active', index === nextIndex);
      });
    }

    goToSlide(index, options = {}) {
      const maxIndex = this.slides.length - 1;
      let nextIndex = index;

      if (options.loop && maxIndex >= 0) {
        if (nextIndex > maxIndex) nextIndex = 0;
        if (nextIndex < 0) nextIndex = maxIndex;
      } else {
        nextIndex = Math.max(0, Math.min(maxIndex, nextIndex));
      }

      this.syncSlideVisualState(nextIndex, { animate: options.animate });
      this.currentIndex = nextIndex;
      this.setTrackOffset(this.getSlideOffset(this.currentIndex));
      this.updateNav({ restartProgress: options.restartProgress !== false });
    }

    syncAutoplayClass() {
      const isRunning =
        this.autoplayEnabled &&
        this.shouldUseMobileCarousel() &&
        !this.prefersReducedMotion &&
        this.autoplayTimer !== null;

      this.section.classList.toggle('is-autoplay-active', isRunning);
    }

    restartDotProgress() {
      const activeDot = this.dots[this.currentIndex];
      if (!activeDot) return;

      activeDot.classList.remove('is-active');
      void activeDot.offsetWidth;
      activeDot.classList.add('is-active');
      activeDot.setAttribute('aria-current', 'true');
    }

    updateNav(options = {}) {
      if (this.prevBtn && this.nextBtn) {
        if (this.shouldUseMobileCarousel()) {
          this.prevBtn.disabled = false;
          this.nextBtn.disabled = false;
        } else {
          this.prevBtn.disabled = this.currentIndex <= 0;
          this.nextBtn.disabled = this.currentIndex >= this.slides.length - 1;
        }
      }

      this.dots.forEach((dot, index) => {
        const isActive = index === this.currentIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      this.syncAutoplayClass();

      if (
        options.restartProgress &&
        this.autoplayEnabled &&
        this.shouldUseMobileCarousel() &&
        !this.prefersReducedMotion &&
        this.autoplayTimer
      ) {
        this.restartDotProgress();
      }
    }

    onPrev() {
      this.pauseAutoplay();
      if (this.shouldUseMobileCarousel()) {
        this.goToSlide(this.currentIndex - 1, { loop: true });
        this.scheduleAutoplayResume();
        return;
      }

      const nextIndex = Math.max(0, this.currentIndex - 1);
      this.goToSlide(nextIndex, { restartProgress: false });
      this.scrollToIndex(nextIndex);
    }

    onNext() {
      this.pauseAutoplay();
      if (this.shouldUseMobileCarousel()) {
        this.goToSlide(this.currentIndex + 1, { loop: true });
        this.scheduleAutoplayResume();
        return;
      }

      const nextIndex = Math.min(this.slides.length - 1, this.currentIndex + 1);
      this.goToSlide(nextIndex, { restartProgress: false });
      this.scrollToIndex(nextIndex);
    }

    onDotClick(event) {
      const dot = event.currentTarget;
      const index = Number.parseInt(dot.dataset.ofShowcaseDot, 10);
      if (Number.isNaN(index)) return;
      this.pauseAutoplay();

      if (this.shouldUseMobileCarousel()) {
        this.goToSlide(index);
        this.scheduleAutoplayResume();
        return;
      }

      this.goToSlide(index, { restartProgress: false });
      this.scrollToIndex(index);
    }

    startAutoplay() {
      this.stopAutoplay();
      if (!this.autoplayEnabled || !this.shouldUseMobileCarousel() || this.prefersReducedMotion) return;
      if (document.hidden) return;

      this.autoplayTimer = window.setTimeout(() => {
        this.autoplayTimer = null;
        this.syncAutoplayClass();
        this.goToSlide(this.currentIndex + 1, { loop: true, restartProgress: false });
        this.startAutoplay();
      }, this.autoplayInterval);

      this.syncAutoplayClass();
      this.restartDotProgress();
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        window.clearTimeout(this.autoplayTimer);
        this.autoplayTimer = null;
      }
      this.syncAutoplayClass();
    }

    pauseAutoplay() {
      this.stopAutoplay();
      if (this.autoplayResumeTimer) {
        window.clearTimeout(this.autoplayResumeTimer);
        this.autoplayResumeTimer = null;
      }
    }

    scheduleAutoplayResume() {
      if (!this.autoplayEnabled || !this.shouldUseMobileCarousel()) return;

      if (this.autoplayResumeTimer) {
        window.clearTimeout(this.autoplayResumeTimer);
      }

      this.autoplayResumeTimer = window.setTimeout(() => {
        this.autoplayResumeTimer = null;
        this.startAutoplay();
      }, this.autoplayInterval);
    }

    syncAutoplay() {
      if (this.shouldUseMobileCarousel() && this.autoplayEnabled) {
        this.startAutoplay();
      } else {
        this.stopAutoplay();
      }
    }

    onTouchStart(event) {
      if (!this.shouldUseMobileCarousel() || event.touches.length !== 1) return;

      this.touchStartX = event.touches[0].clientX;
      this.touchStartY = event.touches[0].clientY;
      this.touchDeltaX = 0;
      this.isSwiping = false;
      this.pauseAutoplay();
    }

    onTouchMove(event) {
      if (!this.shouldUseMobileCarousel() || event.touches.length !== 1) return;

      const dx = event.touches[0].clientX - this.touchStartX;
      const dy = event.touches[0].clientY - this.touchStartY;

      if (!this.isSwiping && Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 10) {
        this.isSwiping = true;
      }

      if (this.isSwiping) {
        this.touchDeltaX = dx;
        event.preventDefault();
      }
    }

    onTouchEnd() {
      if (!this.shouldUseMobileCarousel()) return;

      if (this.isSwiping) {
        if (this.touchDeltaX <= -SWIPE_THRESHOLD) {
          this.goToSlide(this.currentIndex + 1, { loop: true });
        } else if (this.touchDeltaX >= SWIPE_THRESHOLD) {
          this.goToSlide(this.currentIndex - 1, { loop: true });
        }
      }

      this.isSwiping = false;
      this.scheduleAutoplayResume();
    }

    onWheel(event) {
      if (!this.isWheelCaptureActive()) return;

      if (Math.abs(event.deltaY) < 8) return;

      const now = Date.now();
      const direction = event.deltaY > 0 ? 1 : -1;
      const nextIndex = this.currentIndex + direction;
      const canSwitch = nextIndex >= 0 && nextIndex <= this.slides.length - 1;

      if (now < this.wheelLockedUntil) {
        if (canSwitch) event.preventDefault();
        return;
      }

      if (!canSwitch) return;

      event.preventDefault();
      this.wheelLockedUntil = now + this.wheelCooldownMs;
      this.goToSlide(nextIndex, { restartProgress: false });
      this.scrollToIndex(nextIndex);
    }

    onScroll() {
      if (this.isMobileMode() || this.prefersReducedMotion) return;

      if (this.slides.length <= 1 || this.maxScroll <= 0) {
        this.setTrackOffset(0);
        return;
      }

      const internalScrollY = this.getInternalScrollY();
      const nextIndex = this.getIndexFromInternalScrollY(internalScrollY);

      if (!this.wheelAnimating && nextIndex !== this.currentIndex) {
        this.goToSlide(nextIndex, { restartProgress: false });
        return;
      }

      this.setTrackOffset(this.getSlideOffset(this.currentIndex));
    }

    onModeChange() {
      this.currentIndex = 0;
      this.section.classList.remove('is-slide-forward', 'is-slide-backward');
      this.pauseAutoplay();
      this.measure();
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.pauseAutoplay();
        return;
      }

      this.syncAutoplay();
    }

    onResize() {
      this.syncHeaderOffset();
      this.measure();
    }

    bindEvents() {
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.scrollContainer.addEventListener('wheel', this.onWheel, { passive: false });

      if (this.isMobile.addEventListener) {
        this.isMobile.addEventListener('change', this.onModeChange);
      } else {
        this.isMobile.addListener(this.onModeChange);
      }

      if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNext);
      this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick));

      this.viewport.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.viewport.addEventListener('touchmove', this.onTouchMove, { passive: false });
      this.viewport.addEventListener('touchend', this.onTouchEnd, { passive: true });
      this.viewport.addEventListener('touchcancel', this.onTouchEnd, { passive: true });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.onResize());
        this.resizeObserver.observe(this.track);
        this.resizeObserver.observe(this.viewport);
        this.resizeObserver.observe(this.pin);

        const storeHeader = document.querySelector('store-header');
        if (storeHeader) {
          this.headerObserver = new ResizeObserver(() => this.onResize());
          this.headerObserver.observe(storeHeader);
        }
      }

      this.track.querySelectorAll('img, video').forEach((media) => {
        if (media.tagName === 'IMG' && media.complete) return;
        if (media.tagName === 'VIDEO' && media.readyState >= 2) return;

        const eventName = media.tagName === 'VIDEO' ? 'loadeddata' : 'load';
        media.addEventListener(eventName, this.onResize, { once: true });
      });
    }

    destroy() {
      this.pauseAutoplay();
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.scrollContainer.removeEventListener('wheel', this.onWheel);

      if (this.isMobile.removeEventListener) {
        this.isMobile.removeEventListener('change', this.onModeChange);
      } else {
        this.isMobile.removeListener(this.onModeChange);
      }

      if (this.prevBtn) this.prevBtn.removeEventListener('click', this.onPrev);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this.onNext);
      this.dots.forEach((dot) => dot.removeEventListener('click', this.onDotClick));

      this.viewport.removeEventListener('touchstart', this.onTouchStart);
      this.viewport.removeEventListener('touchmove', this.onTouchMove);
      this.viewport.removeEventListener('touchend', this.onTouchEnd);
      this.viewport.removeEventListener('touchcancel', this.onTouchEnd);

      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.headerObserver) this.headerObserver.disconnect();

      this.scrollContainer.style.height = '';
      this.track.style.transform = '';
      this.section.classList.remove('is-ss-ready', 'is-slide-forward', 'is-slide-backward');
      this.slides.forEach((slide) => {
        slide.style.flex = '';
        slide.style.width = '';
        slide.classList.remove('is-active');
      });
    }
  }

  function initShowcaseSliders() {
    document.querySelectorAll('[data-of-showcase-slider]').forEach((section) => {
      const id = section.id || section.dataset.sectionId;
      if (!id) return;

      if (instances.has(id)) {
        instances.get(id).destroy();
      }

      const instance = new OfShowcaseSlider(section);
      if (instance.pin) {
        instances.set(id, instance);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initShowcaseSliders);
  } else {
    initShowcaseSliders();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    if (
      !target.matches?.('[data-of-showcase-slider]') &&
      !target.querySelector?.('[data-of-showcase-slider]')
    ) {
      return;
    }
    initShowcaseSliders();
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector('[data-of-showcase-slider]');
    if (!section?.id || !instances.has(section.id)) return;
    instances.get(section.id).destroy();
    instances.delete(section.id);
  });
})();
